/**
 * Geometry for the video branding overlays (title banner + watermark logo).
 *
 * These values MUST stay identical to the ones in the burner:
 * `videopeel-lambda-gateway/lambda/branding-burn-in-lambda/src/layout.ts`
 *
 * The frontend renders a live CSS preview of the same overlays before the burn runs. If the
 * two sides drift apart the preview lies to the user — they configure one thing and receive
 * another. Any change here needs the matching change there, and vice versa.
 */

/** Distance of the title banner from the top edge, as a fraction of video height. */
export const TITLE_TOP_MARGIN_PCT = 0.03;

/** Title font size, as a fraction of video height. */
export const TITLE_FONT_SIZE_PCT = 0.05;

/** Padding around the title's background box, as a fraction of the font size. */
export const TITLE_BOX_PADDING_PCT = 0.4;

/** Inset of the watermark from its corner, as a fraction of video width. */
export const WATERMARK_MARGIN_PCT = 0.05;

/**
 * The burn draws the title with FFmpeg's `LiberationSans-Regular`. Liberation Sans is
 * metric-compatible with Arial, so Arial (or Helvetica) reproduces its advance widths in the
 * browser — which is what keeps a centred title centred in both the preview and the MP4.
 * Do not replace this with a generic stack; a different font would change the text width and
 * with it the banner's size.
 */
export const TITLE_FONT_FAMILY = 'Arial, "Liberation Sans", Helvetica, sans-serif';

/**
 * Watermark corners, matching the `watermark_position` ENUM in the API
 * (api/db/migrations/20260727100000-create-branding-presets.js) and the
 * WatermarkPosition union in the burner's layout.ts.
 */
export const WATERMARK_POSITIONS = {
  TOP_LEFT: 'top_left',
  TOP_RIGHT: 'top_right',
  BOTTOM_LEFT: 'bottom_left',
  BOTTOM_RIGHT: 'bottom_right'
};

export const WATERMARK_POSITION_VALUES = Object.values(WATERMARK_POSITIONS);

/**
 * Title positions, matching the `title_position` ENUM in the API
 * (api/db/migrations/20260806100000-add-title-position-to-branding-presets.js) and the
 * TitlePosition union in the burner's layout.ts.
 *
 * The video's centre is deliberately absent: it covers the face of whoever is speaking.
 */
export const TITLE_POSITIONS = {
  TOP_LEFT: 'top_left',
  TOP_CENTER: 'top_center',
  TOP_RIGHT: 'top_right',
  BOTTOM_LEFT: 'bottom_left',
  BOTTOM_CENTER: 'bottom_center',
  BOTTOM_RIGHT: 'bottom_right'
};

export const TITLE_POSITION_VALUES = Object.values(TITLE_POSITIONS);

/**
 * Inset of a corner-anchored title from the side edge, as a fraction of video width.
 *
 * Its own constant rather than an alias of WATERMARK_MARGIN_PCT: the two agree today so that a
 * title and a logo in opposite corners share one frame, but they are separate decisions and
 * changing one must not drag the other.
 */
export const TITLE_SIDE_MARGIN_PCT = 0.05;

/** Crop ratios the burner supports. '16:9' is the baseline and applies no crop filter. */
export const CROP_RATIOS = ['16:9', '9:16', '1:1'];

/**
 * Vertical offset of the title **banner** from the top, as a fraction of height.
 *
 * This is deliberately not the same as the burn's `y`. In FFmpeg, `y` positions the top of
 * the *text*, while `box=1` paints the background `boxborderw` pixels above it — so the burn
 * adds the padding to `y` to make the visible banner start at TITLE_TOP_MARGIN_PCT. In CSS
 * the box and its padding are positioned together, so the preview positions the banner
 * directly at TITLE_TOP_MARGIN_PCT and lets padding grow inward.
 *
 * Exported as a function of the rendered height so callers cannot accidentally apply the
 * burn's text-offset formula instead.
 */
export const titleBannerTopPx = (renderedHeight) => renderedHeight * TITLE_TOP_MARGIN_PCT;

/** Title font size in px for a given rendered height. */
export const titleFontSizePx = (renderedHeight) => renderedHeight * TITLE_FONT_SIZE_PCT;

/** Padding of the title box in px, derived from the font size (not the frame height). */
export const titleBoxPaddingPx = (renderedHeight) =>
  titleFontSizePx(renderedHeight) * TITLE_BOX_PADDING_PCT;

/** Title inset from the side edge, in px, for a corner-anchored banner. */
export const titleSideMarginPx = (renderedWidth) => renderedWidth * TITLE_SIDE_MARGIN_PCT;

/** Watermark inset from its corner, in px, for a given rendered width. */
export const watermarkMarginPx = (renderedWidth) => renderedWidth * WATERMARK_MARGIN_PCT;

/** Watermark width in px: `scale` is a fraction of the video width, exactly as in the burn. */
export const watermarkWidthPx = (renderedWidth, scale) => renderedWidth * scale;



// WEBPACK FOOTER //
// ./src/constants/branding-layout.js